// apps/dashboard/src/routes/targets/$targetId/index.tsx
import {
  createScan,
  getTarget,
  listIdentities,
  listScansForTarget,
  type CreateScanBody,
  type Identity,
  type RecentScan,
  type Target,
} from "@/api/client";
import { invalidateScanRelated } from "@/api/invalidate";
import { TargetBreadcrumbBar } from "@/components/targets/detail/TargetBreadcrumbBar";
import { TargetHeader } from "@/components/targets/detail/TargetHeader";
import {
  TargetTabsSection,
  type Tab,
} from "@/components/targets/detail/TargetTabsSection";
import {
  ScanKickoffDialog,
  type ScanConfirmSelection,
} from "@/components/targets/scans/ScanKickoffDialog";
import { RouteShell } from "@/design-system";
import { isLocalUrl } from "@/lib/url";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { consumeOauthCallbackQuery } from "@/lib/oauthCallback";
import { track } from "@/lib/analytics";
import { z } from "zod";

const searchSchema = z.object({
  tab: z
    .enum(["scans", "credentials", "settings", "issues"])
    .optional()
    .catch(undefined),
  // Preserved so the OAuth authorization-code callback params survive
  // validateSearch long enough for consumeOauthCallbackQuery() to read
  // and strip them off window.location.
  identityAdded: z.string().optional().catch(undefined),
  identityError: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/targets/$targetId/")({
  validateSearch: searchSchema,
  component: TargetDetailPage,
});

function TargetDetailPage() {
  const { targetId } = Route.useParams();
  const navigate = useNavigate({ from: "/targets/$targetId/" });
  const queryClient = useQueryClient();

  const {
    data: target,
    isPending,
    error,
  } = useQuery({
    queryKey: ["target", targetId],
    queryFn: ({ signal }) => getTarget(targetId, signal),
  });

  const search = Route.useSearch();
  const tab: Tab = search.tab ?? "scans";

  const setTab = (next: Tab) => {
    if (next !== tab) track({ event: "server_tab_viewed", tab: next });
    void navigate({
      search: (prev) => ({ ...prev, tab: next === "scans" ? undefined : next }),
      replace: true,
    });
  };

  const [kickoff, setKickoff] = useState(false);

  // On mount, consume `?identityAdded` / `?identityError` set by the
  // OAuth authorization-code callback. The helper pushes the outcome
  // onto the global async-banners store (rendered by DashboardShell
  // above every route); we just deep-link to the credentials tab so
  // the user lands where the new identity (or its failure) lives.
  useEffect(() => {
    const outcome = consumeOauthCallbackQuery();
    if (outcome?.focusCredentials) {
      void navigate({
        search: (prev) => ({ ...prev, tab: "credentials" }),
        replace: true,
      });
    }
  }, [navigate]);

  const { data: identitiesData } = useQuery({
    queryKey: ["target", targetId, "identities"],
    queryFn: ({ signal }) => listIdentities(targetId, signal),
  });
  const identities = identitiesData?.identities ?? [];

  const { data: scansData } = useQuery({
    queryKey: ["target", targetId, "scans"],
    queryFn: ({ signal }) => listScansForTarget(targetId, signal),
  });
  const scans = scansData?.scans ?? [];

  const scan = useMutation({
    mutationFn: (input: CreateScanBody) =>
      createScan(targetId, {
        intensity: input.intensity,
        identityIds: input.identityIds,
        adversarialOptIn: input.adversarialOptIn,
      }),
    onSuccess: (data, input) => {
      if (input.intensity) {
        track({
          event: "scan_started",
          intensity: input.intensity,
          identity_count: input.identityIds?.length ?? 0,
          adversarial_opt_in: input.adversarialOptIn ?? false,
          source: "dashboard",
        });
      }
      invalidateScanRelated(queryClient, targetId);
      void navigate({
        to: "/targets/$targetId/scans/$scanId",
        params: { targetId, scanId: data.scanId },
      });
    },
  });

  return (
    <RouteShell
      isPending={isPending}
      error={error}
      loadingLabel="Loading target…"
    >
      {target ? (
        <TargetDetailBody
          target={target}
          targetId={targetId}
          tab={tab}
          setTab={setTab}
          identities={identities}
          scans={scans}
          kickoff={kickoff}
          setKickoff={setKickoff}
          onScanClick={() => setKickoff(true)}
          scanPending={scan.isPending}
          scanError={scan.error}
          onClearScanError={() => scan.reset()}
          onConfirmScan={(s) =>
            scan.mutate({
              identityIds: s.identityIds,
              intensity: s.intensity,
              adversarialOptIn: s.adversarialOptIn,
            })
          }
        />
      ) : null}
    </RouteShell>
  );
}

interface TargetDetailBodyProps {
  target: Target;
  targetId: string;
  tab: Tab;
  setTab: (t: Tab) => void;
  identities: Identity[];
  scans: RecentScan[];
  kickoff: boolean;
  setKickoff: (b: boolean) => void;
  onScanClick: () => void;
  scanPending: boolean;
  scanError: Error | null;
  onClearScanError: () => void;
  onConfirmScan: (s: ScanConfirmSelection) => void;
}

function TargetDetailBody({
  target,
  targetId,
  tab,
  setTab,
  identities,
  scans,
  kickoff,
  setKickoff,
  onScanClick,
  scanPending,
  scanError,
  onClearScanError,
  onConfirmScan,
}: TargetDetailBodyProps) {
  const scanInProgress = scans.some(
    (s) => s.status === "queued" || s.status === "running",
  );
  // Private servers can only be scanned with the CLI — block every scan
  // affordance on this page and surface the CLI banner instead.
  const cliOnly = isLocalUrl(target.url);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <TargetBreadcrumbBar serverName={target.displayName} />

      <TargetHeader
        target={target}
        scanInProgress={scanInProgress}
        pending={scanPending}
        onScan={onScanClick}
        cliOnly={cliOnly}
      />

      {cliOnly ? null : (
        <ScanKickoffDialog
          open={kickoff}
          target={target}
          identities={identities}
          pending={scanPending}
          onCancel={() => setKickoff(false)}
          onConfirm={onConfirmScan}
        />
      )}

      <TargetTabsSection
        target={target}
        targetId={targetId}
        tab={tab}
        setTab={setTab}
        identities={identities}
        scans={scans}
        cliOnly={cliOnly}
        onScanClick={onScanClick}
        scanError={scanError}
        onClearScanError={onClearScanError}
      />
    </div>
  );
}
